<template lang="pug">
.d-flex.flex-column.gap-24
  //- TEMP_SIGNATURE_PREVIEW
  dv-alert(v-if="isPreview" color="ui-info") Тестовый режим: пароль и email сохраняются только локально. Код подтверждения — 123456.
  dv-alert(color="ui-info")
    p.ui-body-1.font-weight-medium Приоритетный способ входа — логин и пароль
    p.ui-body-2.mt-1
      | УКЭП организации используют преимущественно руководители. Сотрудникам рекомендуем входить по логину и паролю.
      | Если вы вошли через УКЭП из-за утраты пароля — задайте новый пароль и проверьте почту для восстановления доступа.
  dv-server-form(
    ref="form"
    v-model="form"
    :schema="schema"
    :method="savePassword"
    :success-callback="onPasswordSaved"
    :error-callback="onPasswordError"
    :nestable="false"
    v-slot="{ fields, submit, loading, invalid }"
  )
    .d-flex.flex-column.gap-24
      dv-alert(v-if="errorMessage" color="ui-error" role="alert") {{ errorMessage }}
      dv-text-field(:value="username" label="Логин для входа" required readonly hide-details autocomplete="username")
        template(#append)
          dv-btn(text :disabled="!username" @click="copyLogin")
            v-icon.mr-2(:size="20") $ui-copy
            span Скопировать
      dv-form-field(:field="fields.password" :disabled="loading")
      dv-form-field(:field="fields.password_confirmation" :disabled="loading")
      p.ui-body-2.opacity-70 Для сохранения пароля потребуется повторное подтверждение УКЭП.
      dv-btn(large :loading="loading || saving" :disabled="invalid || !hasChanges || loading || saving" @click="submit") Сохранить пароль
    portal(:to="`${dialogId}.${$dialogs.slots.actions}`" v-slot="{ close }")
      .d-flex.flex-wrap.justify-end.gap-16.full-width
        dv-btn(large outlined @click="close") Закрыть
  v-divider
  .d-flex.flex-column.gap-12
    template(v-if="!editingEmail")
      p.ui-body-1.font-weight-medium Email
      .d-flex.flex-wrap.align-center.gap-16
        p.ui-body-1.opacity-70 {{ email || 'Не указан' }}
        dv-btn(v-if="!emailChanges.length" large text @click="openEmailEditor()") Изменить
    signature-email-form(
      v-else
      :context="context"
      :initial="email"
      :pending="selectedEmailRequest"
      :requests="emailChanges"
      :is-active="isActive"
      @request="onEmailRequest"
      @success="onEmailSaved"
      @cancel="editingEmail = false"
      @refresh="localEmailChanges = null"
    )
    email-change-requests-list(
      :items="emailChanges"
      :class="{ 'ui-background pa-3 rounded-12': emailChanges.length }"
      :busy="editingEmail"
      :remove-method="removeEmailRequest"
      :is-active="isActive"
      @confirm="openEmailEditor"
      @removed="onEmailRequestRemoved"
    )
</template>

<script>
import DvAlert from '@/packages/ui/components/alerts/DvAlert.vue';
import DvBtn from '@/packages/ui/components/buttons/DvBtn.vue';
import DvFormField from '@/packages/ui/components/forms/DvFormField.vue';
import DvServerForm from '@/packages/ui/components/forms/DvServerForm.vue';
import DvTextField from '@/packages/ui/components/inputs/DvTextField.vue';
import DvPasswordField from '@/packages/ui/components/inputs/DvPasswordField.vue';
import { copyTextToClipboard } from '@/helpers/text';
import getApiErrorMessage from '@/helpers/getApiErrorMessage';
import { assertActive, SignatureClientError } from '../../services/cryptoPro';
import EmailChangeRequestsList from '@/modules/UserProfile/components/EmailChangeRequestsList.vue';
// TEMP_SIGNATURE_PREVIEW
import { isSignaturePreviewContext } from '../../services/signaturePreview';
import SignatureEmailForm from './SignatureEmailForm.vue';

export default {
  components: { DvAlert, DvBtn, DvFormField, DvServerForm, DvTextField, SignatureEmailForm, EmailChangeRequestsList },
  props: {
    context: { type: Object, required: true },
    dialogId: { type: String, required: true },
    isActive: { type: Function, required: true },
  },
  data: () => ({ form: { password: '', password_confirmation: '' }, errorMessage: '', saving: false, editingEmail: false, localEmailChanges: null, selectedEmailRequest: null }),
  computed: {
    // TEMP_SIGNATURE_PREVIEW: on removal keep accountData returning this.$user.data.
    isPreview() { return isSignaturePreviewContext(this.context); },
    accountData() { return this.isPreview ? this.context.previewUser : this.$user.data; },
    username() { return this.accountData.username || ''; },
    email() { return this.accountData.organizer?.contact_email || ''; },
    emailChanges() { return this.localEmailChanges || this.accountData.change_emails || []; },
    hasChanges() { return !!this.form.password || !!this.form.password_confirmation; },
    schema() {
      return [
        { name: 'password', value: 'password', label: 'Пароль', rules: 'required|hard_password', component: DvPasswordField, props: { placeholder: 'Введите придуманный пароль', autocomplete: 'new-password' } },
        { name: 'password_confirmation', value: 'password_confirmation', label: 'Повторите пароль', rules: 'required|confirmed:password', component: DvPasswordField, props: { placeholder: 'Повторите пароль', autocomplete: 'new-password' } },
      ];
    },
  },
  beforeDestroy() {
    this.form = { password: '', password_confirmation: '' };
  },
  methods: {
    async copyLogin() {
      try {
        if (navigator.clipboard?.writeText) await navigator.clipboard.writeText(this.username);
        else copyTextToClipboard(this.username);
        if (this.isActive()) this.$toast.success('Логин скопирован');
      } catch (error) {
        this.$toast.error('Не удалось скопировать логин');
      }
    },
    async savePassword() {
      if (this.saving || !this.isActive() || !this.hasChanges) return { error: new SignatureClientError('Операция отменена.') };
      this.saving = true;
      this.errorMessage = '';
      try {
        const result = await this.$models.auth.signature.setupPassword({
          data: { ...this.form },
          thumbprint: this.context.thumbprint,
          organizationId: this.context.organizationId,
          isActive: this.isActive,
          obs: this.$refs.form.$refs.form.$refs.obs,
        });
        assertActive(this.isActive);
        return result;
      } catch (error) {
        return { error };
      } finally {
        this.saving = false;
      }
    },
    onPasswordSaved({ reset }) {
      if (!this.isActive()) return;
      reset({ password: '', password_confirmation: '' });
      this.$toast.success('Пароль сохранён');
    },
    onPasswordError({ data }) {
      if (this.isActive()) this.errorMessage = data?.signatureClientError ? data.message : getApiErrorMessage(data);
    },
    onEmailSaved(data) {
      if (!this.isActive()) return;
      this.onEmailRequest(data);
      this.editingEmail = false;
    },
    openEmailEditor(item = null) {
      if (!this.isActive() || this.editingEmail || item?.change_at || (!item && this.emailChanges.length)) return;
      this.selectedEmailRequest = item;
      this.editingEmail = true;
    },
    setEmailChanges(items) {
      this.localEmailChanges = items;
      // TEMP_SIGNATURE_PREVIEW: the local model already updated the dummy account.
      if (!this.isPreview) this.$user.patch({ change_emails: items });
    },
    onEmailRequest(data) {
      if (this.isActive() && data?.new) this.setEmailChanges([...this.emailChanges.filter(({ id }) => id !== data.id), data]);
    },
    removeEmailRequest(params) {
      return this.$models.auth.accountSetup.removeEmailChange({ ...params, organizationId: this.context.organizationId });
    },
    onEmailRequestRemoved(id) {
      if (this.isActive()) this.setEmailChanges(this.emailChanges.filter((item) => item.id !== id));
    },
  },
};
</script>
